
import { Component, Vue } from 'vue-property-decorator'
import { AdminApi } from '@/wwapi/apis/admin-api'
import { cloneDeep } from 'lodash'
import { Form } from 'element-ui'

interface IConfigData {
  id?: number
  configName: string
  configKey: string
  configValue: string | null
  countries: string | null
  online: boolean
}

const defaultConfigData: IConfigData = {
  configName: '',
  configKey: '',
  configValue: null,
  countries: null,
  online: true
}

@Component({
  name: 'ConfigList'
})
export default class extends Vue {
  private list: IConfigData[] = []
  private listLoading = true
  private dialogFormVisible = false
  private dialogStatus = ''
  private deleteConfirmationVisible = false
  private deleteItemId: number | null = null
  private tempConfigData: IConfigData = cloneDeep(defaultConfigData)

  private rules = {
    configKey: [
      { required: true, message: 'Config key is required', trigger: 'blur' }
    ]
  }

  created() {
    this.fetchData()
  }

  private async fetchData() {
    this.listLoading = true
    try {
      const adminApi = new AdminApi()
      const response = await adminApi.getConfigs()
      if (response.data.data) {
        this.list = response.data.data
      } else {
        this.list = []
      }
    } catch (error) {
      console.error('Error fetching configs:', error)
      this.$notify({
        title: 'Error',
        message: 'Failed to load configurations',
        type: 'error',
        duration: 2000
      })
      this.list = []
    } finally {
      this.listLoading = false
    }
  }

  showDeleteConfirmation(row: IConfigData) {
    this.deleteItemId = row.id!
    this.deleteConfirmationVisible = true
  }

  async deleteItem() {
    try {
      const adminApi = new AdminApi()
      const { data } = await adminApi.deleteConfig(this.deleteItemId!)
      if (data.status.code === 'success') {
        const index = this.list.findIndex((v: IConfigData) => v.id === this.deleteItemId)
        this.list.splice(index, 1)
        this.$notify({
          title: 'Success',
          message: 'Config deleted successfully',
          type: 'success',
          duration: 2000
        })
      }
    } catch (error: any) {
      this.$notify({
        title: 'Error',
        message: error.response?.data?.status?.message || 'Failed to delete config',
        type: 'error',
        duration: 2000
      })
    } finally {
      this.deleteItemId = null
      this.deleteConfirmationVisible = false
    }
  }

  private handleUpdate(row: IConfigData) {
    this.tempConfigData = Object.assign({}, row)
    this.dialogStatus = 'update'
    this.dialogFormVisible = true
    this.$nextTick(() => {
      (this.$refs.dataForm as Form).clearValidate()
    })
  }

  private updateData() {
    const adminApi = new AdminApi()
    ;(this.$refs.dataForm as Form).validate(async(valid) => {
      if (valid) {
        try {
          const { data } = await adminApi.updateConfig(
            this.tempConfigData.id!,
            {
              configName: this.tempConfigData.configName || null,
              configKey: this.tempConfigData.configKey,
              configValue: this.tempConfigData.configValue || null,
              countries: this.tempConfigData.countries || null,
              online: this.tempConfigData.online
            }
          )
          if (data.status.code === 'success') {
            const index = this.list.findIndex((v: IConfigData) => v.id === data.data.id)
            this.list.splice(index, 1, data.data)
            this.dialogFormVisible = false
            this.$notify({
              title: 'Success',
              message: 'Config updated successfully',
              type: 'success',
              duration: 2000
            })
          }
        } catch (error: any) {
          this.$notify({
            title: 'Error',
            message: error.response?.data?.status?.message || 'Failed to update config',
            type: 'error',
            duration: 2000
          })
        }
      }
    })
  }

  private handleCreate() {
    this.tempConfigData = cloneDeep(defaultConfigData)
    this.dialogStatus = 'create'
    this.dialogFormVisible = true
    this.$nextTick(() => {
      (this.$refs.dataForm as Form).clearValidate()
    })
  }

  private createData() {
    const adminApi = new AdminApi()
    ;(this.$refs.dataForm as Form).validate(async(valid) => {
      if (valid) {
        try {
          const { data } = await adminApi.createConfig({
            configName: this.tempConfigData.configName || null,
            configKey: this.tempConfigData.configKey,
            configValue: this.tempConfigData.configValue || null,
            countries: this.tempConfigData.countries || null,
            online: this.tempConfigData.online
          })
          if (data.status.code === 'success') {
            this.list = [...this.list, data.data]
            this.dialogFormVisible = false
            this.$notify({
              title: 'Success',
              message: 'Config created successfully',
              type: 'success',
              duration: 2000
            })
          }
        } catch (error: any) {
          this.$notify({
            title: 'Error',
            message: error.response?.data?.status?.message || 'Failed to create config',
            type: 'error',
            duration: 2000
          })
        }
      }
    })
  }

  private async toggleConfigStatus(row: IConfigData, online: boolean) {
    try {
      const adminApi = new AdminApi()
      const { data } = await adminApi.updateConfig(row.id!, {
        configName: row.configName || null,
        configKey: row.configKey,
        configValue: row.configValue || null,
        countries: row.countries,
        online: online
      })
      if (data.status.code === 'success') {
        row.online = online
        const index = this.list.findIndex((v: IConfigData) => v.id === row.id)
        this.list.splice(index, 1, row)
        this.$notify({
          title: 'Success',
          message: 'Config status updated successfully',
          type: 'success',
          duration: 2000
        })
      }
    } catch (error: any) {
      this.$notify({
        title: 'Error',
        message: error.response?.data?.status?.message || 'Failed to update status',
        type: 'error',
        duration: 2000
      })
    }
  }
}
